import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, forwardRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { Backdrop, Typography, useMediaQuery } from '@mui/material';
import AppLoader from '../../../components/AppLoader/AppLoader';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { tableStyleOverDue, tableStyle } from '../../../style/common';
import "../../../style/common.scss";
import {
    WORKFLOW_MOBILE_MEDIA,
    workflowDialogClassName,
    workflowDialogShellProps,
} from '../../../utils/workflowDialogShell';
import { useDispatch, useSelector } from 'react-redux';
import ServiceDashboardTables from '../../../components/ServiceDashboardTables/ServiceDashboardTables';
import Error from '../../../components/error/error';
import { SearchIndividualApplication } from '../../../network/APIs/APIs';
import { numberFormatting } from '../../../services/Functions';
import { getApplicationProgressStatus } from '../../../utils/applicationProgressStatus';
import {
    fetchPaginatedDisconnectionNormal,
    filterDateFrom as filterDateFromNormal,
    filterDateTo as filterDateToNormal,
    filterObjectionStatus as filterObjectionStatusNormal,
    filterProgressId as filterProgressIdNormal,
    firstPage as firstPageNormal,
    lastPage as lastPageNormal,
    nextPage as nextPageNormal,
    previousPage as previousPageNormal,
    resetDateFilter as resetDateFilterNormal,
    resetObjectionStatusFilter as resetObjectionStatusFilterNormal,
    resetProgressIdFilter as resetProgressIdFilterNormal,
} from '../../../features/generalServices/dcNormalSlice';
import {
    fetchPaginatedDisconnectionOverdue,
    filterDateFrom as filterDateFromOverdue,
    filterDateTo as filterDateToOverdue,
    filterObjectionStatus as filterObjectionStatusOverdue,
    filterProgressId as filterProgressIdOverdue,
    firstPage as firstPageOverdue,
    lastPage as lastPageOverdue,
    nextPage as nextPageOverdue,
    previousPage as previousPageOverdue,
    resetDateFilter as resetDateFilterOverdue,
    resetObjectionStatusFilter as resetObjectionStatusFilterOverdue,
    resetProgressIdFilter as resetProgressIdFilterOverdue,
} from '../../../features/generalServices/dcOverdueSlice';
import { getInitialAdminOfficeCode } from '../../../utils/adminCompany';
import { DISCO_COMPANIES } from '../../../constants/discoCompanies';

export const DISCONNECTION_APPLICATION_TYPE_ID = 14;

const EMPTY_DC_STATE = {
    currentPage: 1,
    data: [],
    pageSize: 20,
    totalPages: null,
    totalRecords: null,
    dateFrom: '',
    dateTo: '',
    applicationProgressId: '',
    objectionStatus: '',
    isLoading: false,
};

const convertDate = (dateString) => {
    if (!dateString) return '-';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return `${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()}`;
};

const statusLabel = (item = {}) =>
    getApplicationProgressStatus(
        DISCONNECTION_APPLICATION_TYPE_ID,
        item.applicationProgressId,
        item.objectionStatus,
        item.applicationStatusId || 'In Process',
        'Disconnection',
    );

const getClassName = (x) => {
    if (x === "Pending" || x === "In Process" || x === "Application Submitted") {
        return 'pending-button';
    }
    if (x === "Objection") {
        return 'objection-button';
    }
    if (x === "Rejected" || x === "Request Declined") {
        return 'rejected-button';
    }
    if (x === "Approved" || x === "Completed") {
        return 'approved-button';
    }
    return 'inprocess-button';
};

const mapRow = (item) => ({
    applicationId: item.applicationId,
    creationDate: convertDate(item.creationDate),
    referenceNo: item.referenceNo || item.refNo || '-',
    name: item.applicantName || item.consumerName || item.name || '-',
    disconnectionType: item.disconnectionType || item.dcoType || '-',
    applicationStatusId: statusLabel(item),
    applicationProgressId: item.applicationProgressId,
    releventApplicationFlage: item.releventApplicationFlage,
});

const mapRows = (rows) =>
    (Array.isArray(rows) ? rows : []).filter((item) => item?.applicationId).map(mapRow);

const DC_MOBILE_FIELDS = ["applicationId", "name", "applicationStatusId"];

const DisconnectionTable = forwardRef((props, ref) => {
    const { token, code: officeCode, selectedAdminCompanyCode } = useSelector((state) => state.auth);
    const isWorkflowMobile = useMediaQuery(WORKFLOW_MOBILE_MEDIA);
    const [selectedOfficeCode, setSelectedOfficeCode] = useState(
        getInitialAdminOfficeCode(officeCode, selectedAdminCompanyCode),
    );
    const apiOfficeCode = String(officeCode) === "10"
        ? selectedOfficeCode
        : (selectedOfficeCode || officeCode);

    const [error, setError] = useState();
    const [open, setOpen] = useState(false);
    const [dialogRowData, setDialogRowData] = useState({});
    const [viewBackdrop, setViewBackdrop] = useState(false);
    const location = useLocation();
    const foundId = location.state?.foundId;
    const openedFoundIdRef = useRef(null);
    const currentPath = location.pathname;
    const dispatch = useDispatch();
    const showDashboard = Boolean(props.showTable) && currentPath === "/disconnection-table";

    const dcNormal = useSelector((state) => state.dcNormalPaginated) || EMPTY_DC_STATE;
    const dcOverdue = useSelector((state) => state.dcOverduePaginated) || EMPTY_DC_STATE;

    const {
        currentPage: currentPageNormal,
        data: dataNormal,
        pageSize: pageSizeNormal,
        totalPages: totalPagesNormal,
        totalRecords: totalRecordsNormal,
        dateFrom: dateFromNormal,
        dateTo: dateToNormal,
        applicationProgressId: applicationProgressIdNormal,
        objectionStatus: objectionStatusNormal,
        isLoading: isLoad,
    } = dcNormal;
    const {
        currentPage: currentPageOverdue,
        data: dataOverdue,
        pageSize: pageSizeOverdue,
        totalPages: totalPagesOverdue,
        totalRecords: totalRecordsOverdue,
        dateFrom: dateFromOverdue,
        dateTo: dateToOverdue,
        applicationProgressId: applicationProgressIdOverdue,
        objectionStatus: objectionStatusOverdue,
        isLoading: isLoadd,
    } = dcOverdue;

    const applicationsNormalToDataGrid = useMemo(() => mapRows(dataNormal), [dataNormal]);
    const applicationsOverdueToDataGrid = useMemo(() => mapRows(dataOverdue), [dataOverdue]);

    useEffect(() => {
        if (String(officeCode) === "10" && selectedAdminCompanyCode) {
            const nextCode = String(selectedAdminCompanyCode);
            setSelectedOfficeCode((prev) => (prev === nextCode ? prev : nextCode));
        }
    }, [officeCode, selectedAdminCompanyCode]);

    const handleRowClick = useCallback(async (applicationId) => {
        const totalArray = (Array.isArray(dataNormal) ? dataNormal : [])
            .concat(Array.isArray(dataOverdue) ? dataOverdue : []);
        let itemFound = totalArray.find((item) => item.applicationId === applicationId);
        if (!itemFound) {
            itemFound = await SearchIndividualApplication(
                token,
                applicationId,
                String(DISCONNECTION_APPLICATION_TYPE_ID),
            );
        }
        if (itemFound) {
            setDialogRowData(itemFound);
            setOpen(true);
        }
    }, [dataNormal, dataOverdue, token]);

    const handleClose = () => {
        setOpen(false);
        setDialogRowData({});
        setError('');
        setViewBackdrop(false);
    };

    const handleNextPage = () => dispatch(nextPageNormal());
    const handlePreviousPage = () => dispatch(previousPageNormal());
    const handleFirstPage = () => dispatch(firstPageNormal());
    const handleLastPage = (page) =>
        dispatch(lastPageNormal(typeof page === 'number' ? page : totalPagesNormal));
    const handleDateFromFilter = (value) => { if (value) dispatch(filterDateFromNormal(value)); };
    const handleDateToFilter = (value) => { if (value) dispatch(filterDateToNormal(value)); };
    const handleResetDateFilter = () => dispatch(resetDateFilterNormal());
    const handleProgressIdFilter = (value) => {
        if (value || value === 0) dispatch(filterProgressIdNormal(value));
    };
    const handleResetProgressIdFilter = () => dispatch(resetProgressIdFilterNormal());
    const handleObjectionStatusFilter = (value) => {
        if (value || value === 0) dispatch(filterObjectionStatusNormal(value));
    };
    const handleResetObjectionStatusFilter = () => dispatch(resetObjectionStatusFilterNormal());

    const handleNextPageOverdue = () => dispatch(nextPageOverdue());
    const handlePreviousPageOverdue = () => dispatch(previousPageOverdue());
    const handleFirstPageOverdue = () => dispatch(firstPageOverdue());
    const handleLastPageOverdue = (page) =>
        dispatch(lastPageOverdue(typeof page === 'number' ? page : totalPagesOverdue));
    const handleDateFromFilterOverdue = (value) => { if (value) dispatch(filterDateFromOverdue(value)); };
    const handleDateToFilterOverdue = (value) => { if (value) dispatch(filterDateToOverdue(value)); };
    const handleResetDateFilterOverdue = () => dispatch(resetDateFilterOverdue());
    const handleProgressIdFilterOverdue = (value) => {
        if (value || value === 0) dispatch(filterProgressIdOverdue(value));
    };
    const handleResetProgressIdFilterOverdue = () => dispatch(resetProgressIdFilterOverdue());
    const handleObjectionStatusFilterOverdue = (value) => {
        if (value || value === 0) dispatch(filterObjectionStatusOverdue(value));
    };
    const handleResetObjectionStatusFilterOverdue = () => dispatch(resetObjectionStatusFilterOverdue());

    useEffect(() => {
        if (!showDashboard || !token) return;
        dispatch(fetchPaginatedDisconnectionNormal({
            page: currentPageNormal,
            pageSize: pageSizeNormal,
            dateFrom: dateFromNormal,
            dateTo: dateToNormal,
            loadFrom: '',
            loadTo: '',
            applicationProgressId: applicationProgressIdNormal,
            objectionStatus: objectionStatusNormal,
            officeCode: apiOfficeCode,
        }));
    }, [
        showDashboard,
        currentPageNormal,
        pageSizeNormal,
        dateFromNormal,
        dateToNormal,
        applicationProgressIdNormal,
        objectionStatusNormal,
        apiOfficeCode,
        token,
        dispatch,
    ]);

    useEffect(() => {
        if (!showDashboard || !token) return;
        dispatch(fetchPaginatedDisconnectionOverdue({
            page: currentPageOverdue,
            pageSize: pageSizeOverdue,
            dateFrom: dateFromOverdue,
            dateTo: dateToOverdue,
            applicationProgressId: applicationProgressIdOverdue,
            objectionStatus: objectionStatusOverdue,
            officeCode: apiOfficeCode,
        }));
    }, [
        showDashboard,
        currentPageOverdue,
        pageSizeOverdue,
        dateFromOverdue,
        dateToOverdue,
        applicationProgressIdOverdue,
        objectionStatusOverdue,
        apiOfficeCode,
        token,
        dispatch,
    ]);

    useEffect(() => {
        if (!showDashboard) return;
        if (!foundId || openedFoundIdRef.current === foundId) return;
        openedFoundIdRef.current = foundId;
        handleRowClick(foundId);
    }, [showDashboard, foundId, handleRowClick]);

    const tblColumns = useMemo(() => [
        { field: "applicationId", headerName: 'Application No.', align: 'center', sortable: false, filterable: false, headerStyle: tableStyle.headerStyle, headerCellStyle: tableStyle.headerCellStyle, rowCellStyle: tableStyle.rowCellStyle },
        { field: "creationDate", type: 'dateTime', headerName: 'Submission Date', align: 'center', sortable: false, filterable: false, headerStyle: tableStyle.headerStyle, headerCellStyle: tableStyle.headerCellStyle, rowCellStyle: tableStyle.rowCellStyle },
        { field: "referenceNo", headerName: 'Reference No.', align: 'center', sortable: false, filterable: false, headerStyle: tableStyle.headerStyle, headerCellStyle: tableStyle.headerCellStyle, rowCellStyle: tableStyle.rowCellStyle },
        { field: "name", headerName: 'Applicant Name', align: 'left', sortable: false, filterable: false, headerStyle: tableStyle.headerStyle, headerCellStyle: tableStyle.headerCellStyle, rowCellStyle: tableStyle.rowCellStyle },
        { field: "disconnectionType", headerName: 'Type', align: 'center', sortable: false, filterable: false, headerStyle: tableStyle.headerStyle, headerCellStyle: tableStyle.headerCellStyle, rowCellStyle: tableStyle.rowCellStyle },
        {
            field: "applicationStatusId",
            headerStyle: tableStyle.headerStyle,
            headerCellStyle: tableStyle.headerCellStyle,
            rowCellStyle: tableStyle.rowCellStyle,
            headerName: "Status",
            align: 'center',
            sortable: false,
            filterable: true,
            type: 'singleSelect',
            valueOptions: ['In Process', 'Approved', 'Rejected'],
            renderCell: (params) => {
                const btnText = params.applicationStatusId;
                return <div className={getClassName(btnText)}>{btnText}</div>;
            }
        },
    ], []);

    const tblColumnsOverDue = useMemo(() => tblColumns.map((col) => ({
        ...col,
        headerStyle: tableStyleOverDue.headerStyle,
        headerCellStyle: tableStyleOverDue.headerCellStyle,
        rowCellStyle: tableStyleOverDue.rowCellStyle,
    })), [tblColumns]);

    useImperativeHandle(ref, () => ({
        handleRowClick
    }), [handleRowClick]);

    const detailRows = [
        ['Application No.', dialogRowData.applicationId],
        ['Reference No.', dialogRowData.referenceNo || dialogRowData.refNo],
        ['Applicant Name', dialogRowData.applicantName || dialogRowData.consumerName || dialogRowData.name],
        ['Type', dialogRowData.disconnectionType || dialogRowData.dcoType],
        ['Status', statusLabel(dialogRowData)],
        ['Submission Date', convertDate(dialogRowData.creationDate)],
    ];

    return (
        <>
            <ServiceDashboardTables
                enabled={showDashboard}
                comfortableRows
                isLoadingNormal={Boolean(isLoad)}
                isLoadingOverdue={Boolean(isLoadd)}
                normalTitle={`Disconnection ( ${numberFormatting(totalRecordsNormal) ? numberFormatting(totalRecordsNormal) : "-"} )`}
                overdueCount={numberFormatting(totalRecordsOverdue) ? numberFormatting(totalRecordsOverdue) : "-"}
                normalColumns={tblColumns}
                overdueColumns={tblColumnsOverDue}
                normalData={applicationsNormalToDataGrid}
                overdueData={applicationsOverdueToDataGrid}
                onRowClick={handleRowClick}
                mobileFields={DC_MOBILE_FIELDS}
                showCompanyFilter={officeCode == 10}
                companies={DISCO_COMPANIES}
                selectedOfficeCode={selectedOfficeCode}
                onOfficeCodeChange={setSelectedOfficeCode}
                normalTableProps={{
                    firstPage: handleFirstPage,
                    lastPage: handleLastPage,
                    totalPages: totalPagesNormal,
                    totalRecords: totalRecordsNormal,
                    currentPage: currentPageNormal,
                    handleFilterDateFrom: handleDateFromFilter,
                    handleFilterDateTo: handleDateToFilter,
                    resetDateFilter: handleResetDateFilter,
                    handleFilterProgressId: handleProgressIdFilter,
                    handleFilterObjectionStatus: handleObjectionStatusFilter,
                    resetProgressIdFilter: handleResetProgressIdFilter,
                    resetObjectionStatusFilter: handleResetObjectionStatusFilter,
                    nextPage: handleNextPage,
                    previousPage: handlePreviousPage,
                }}
                overdueTableProps={{
                    firstPage: handleFirstPageOverdue,
                    nextPage: handleNextPageOverdue,
                    previousPage: handlePreviousPageOverdue,
                    lastPage: handleLastPageOverdue,
                    handleFilterDateFrom: handleDateFromFilterOverdue,
                    handleFilterDateTo: handleDateToFilterOverdue,
                    resetDateFilter: handleResetDateFilterOverdue,
                    resetProgressIdFilter: handleResetProgressIdFilterOverdue,
                    resetObjectionStatusFilter: handleResetObjectionStatusFilterOverdue,
                    handleFilterProgressId: handleProgressIdFilterOverdue,
                    handleFilterObjectionStatus: handleObjectionStatusFilterOverdue,
                    totalPages: totalPagesOverdue,
                    totalRecords: totalRecordsOverdue,
                    currentPage: currentPageOverdue,
                }}
            />

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                {...workflowDialogShellProps(isWorkflowMobile)}
                className={workflowDialogClassName('main-dialog', isWorkflowMobile)}
            >
                <DialogTitle className="dialog-title">
                    <div className="dde-dialog-header">
                        <Typography className="applicationTitleText">
                            Disconnection - {dialogRowData.applicationId || ''}
                        </Typography>
                        <button type="button" className="closeIcon" onClick={handleClose} aria-label="Close dialog">
                            <CloseIcon id="clearPopup" sx={{ color: 'white' }} />
                        </button>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <div style={{ padding: '12px 8px 8px' }}>
                        {detailRows.map(([label, value]) => (
                            <Typography key={label} sx={{ mb: 1 }}>
                                <strong>{label}:</strong> {value || '-'}
                            </Typography>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {error ? (
                <Error
                    error={error}
                    onClose={() => setError('')}
                    backgroundColor="#FFC0CB"
                    textColor="#bb0303"
                    iconColor="#bb0303"
                />
            ) : null}

            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                open={viewBackdrop}
            >
                <AppLoader />
            </Backdrop>
        </>
    );
});

DisconnectionTable.displayName = 'DisconnectionTable';

export default DisconnectionTable;
